import { useDisclosure, useToast } from '@chakra-ui/react'
import type React from 'react'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import AddCircleIcon from '../../../../../assets/icons/add-circle-stroke-rounded'
import InfoBox from '../../../../../components/InfoBox'
import PageWrapper from '../../../../../components/Pagewrapper'
import Table from '../../../../../components/Table/Table'
import { useGetAllAffiliatePartnersQuery } from '../../../../../redux/services/coreApi'
import { setAffiliatePartnersTableState } from '../../../../../redux/slices/tableFiltersSlice'
import { RootState } from '../../../../../redux/store'
import type { AffiliatePartnerDto } from '../../../../../types/coreApi-types'
import AddAffiliatePartnerModal from './components/AddAffiliatePartnerModal'

type AffiliatePartnersScreenProps = {}

const AffiliatePartnersScreen: React.FC<AffiliatePartnersScreenProps> = () => {
  const toast = useToast()
  const history = useHistory()
  const { data: affiliatePartners, isLoading } =
    useGetAllAffiliatePartnersQuery()

  const [chosenAffiliatePartner, setChosenAffiliatePartner] =
    useState<AffiliatePartnerDto | null>(null)
  const addAffiliatePartnerModal = useDisclosure()

  const affiliatePartnersTableState = useSelector(
    (state: RootState) => state.affiliatePartnersTable
  )

  return (
    <PageWrapper title="Affiliate Partners">
      <Table
        tableState={affiliatePartnersTableState}
        setTableState={setAffiliatePartnersTableState}
        navigate={() => null}
        isLoading={isLoading}
        onClickRow={v => {
          if (!v) return
          history.push(`/app/admin/affiliate-partners/${v.id}`)
        }}
        emptyDataRender={
          <div className="text-xl mb-3">No affiliate partners found</div>
        }
        rowMenuActions={[
          {
            title: 'See details',
            onClick: v => {
              history.push(`/app/admin/affiliate-partners/${v.id}`)
            },
            hidden: v => false,
          },
        ]}
        defaultData={[
          ...(affiliatePartners?.map(item => {
            return {
              ...item,
              name: item.name,
              contactEmail: item.contactEmail,
              status: item.isActive ? 'Active' : 'Inactive',
              commissionRate: `${(item.commissionRate * 100).toFixed(2)}%`,
            }
          }) ?? []),
        ]}
        schema={[
          {
            cardTitle: true,
            header: 'Partner Name',
            accessorKey: 'name',
            type: 'string',
            priority: 1,
            newlyAdded: 'createdAt',
          },
          {
            header: 'Contact Email',
            accessorKey: 'contactEmail',
            type: 'string',
            priority: 2,
          },
          {
            header: 'Status',
            accessorKey: 'status',
            type: 'status',
            priority: 3,
            cellValueWrapper: (value, originalValue, { original }) => (
              <div className="w-min">
                <InfoBox colorScheme={value === 'Active' ? 'green' : 'red'}>
                  {value}
                </InfoBox>
              </div>
            ),
          },
          {
            header: 'Commission Rate',
            accessorKey: 'commissionRate',
            type: 'string',
            priority: 4,
          },
          {
            header: 'Partnership Start',
            accessorKey: 'partnershipStartDate',
            type: 'date',
            priority: 7,
          },
          {
            header: 'Date Created',
            accessorKey: 'createdAt',
            type: 'date',
            priority: 8,
          },
        ]}
      />

      <div className="flex justify-end mt-6">
        <button
          className="button flex items-center gap-2 bg-primary text-white hover:bg-primary-dark transition-colors"
          onClick={addAffiliatePartnerModal.onOpen}
        >
          <AddCircleIcon width={20} height={20} color="white" />
          <span>Add Affiliate Partner</span>
        </button>
      </div>

      <AddAffiliatePartnerModal
        isOpen={addAffiliatePartnerModal.isOpen}
        onClose={addAffiliatePartnerModal.onClose}
      />
    </PageWrapper>
  )
}

export default AffiliatePartnersScreen
